import { ActionReducerMapBuilder, AnyAction, createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { pageDataService } from 'common';
import { formatUTCDateISO } from 'common/utilities/MiscHelpers';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { testBreachesForOTSv2 } from 'features/identity/OneTimeScan/v2/constants';
import { fromPairs } from 'lodash';
import {
  BreachedDetail,
  BreachEntity,
  DeleteSubscriberCyberDataRequest,
  GetIdVerificationApiRequest,
  ItmUserProfilePatchRequest,
  PhoneGenerateCodeRequest,
  PhoneVerificationCodeRequest,
  RecommendationId,
  RegisterSubscriberWithPrimaryDataRequest,
  ScanRequest,
  SubmitIdVerificationDataApiRequest,
  UpdateSubscriberRequest,
} from 'mcsclient/models';
import { resetAlerts } from 'store/alerts/alertsSlice';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

import { BreachEntityState, BreachEntityType, BreachState } from './breach.types';
import { buildBreaches, mergeBreaches } from './breachHelpers';
import {
  augmentBreach,
  AugmentedBreach,
  extractBreachEntities,
  findBreach,
  getBreachById,
  getBreachEntities,
  getBreachEntityById,
  getBreaches,
  getBreachRecommendationById,
  getBreachStateCounts,
  getEntityRecommendationById,
} from './breachSelectors';
import {
  handleCompleteCreditRecommendationAsync,
  handleGetCreditAlertByIdAsync,
  handleGetCreditSummaryAsync,
  handleUpdateCreditAlertAsync,
} from './creditMonitoringSlice';
import { CodeVerificationStatus, IFamilyMember, IItpUserContext, SSNVerificationStatus } from './identityMonitoring.types';
import { ensureId } from './identityMonitoringHelpers';
import { getITPFamilyMemberFromCsid, getITPFamilyMemberFromUserId } from './identityMonitoringSelectors';
import {
  createDefaultFamilyMember,
  createDefaultSelectedUserOnboarding,
  getDefaultIdentityMonitoringState,
  IIdentityMonitoringState,
} from './identityMonitoringState';

const initialState: IIdentityMonitoringState = getDefaultIdentityMonitoringState();

/**
 * Get family member from state or create a new default one if it doesn't exist
 *
 * @param state
 * @param csid
 */
export function ensureITPFamilyMemberWithCsid(state: IIdentityMonitoringState, csid: string): IFamilyMember {
  let member = getITPFamilyMemberFromCsid(state, csid);
  if (!member) {
    member = createDefaultFamilyMember({ csid });
    state.familyMembers.push(member);
  }
  return member;
}

function ensureITPFamilyMemberWithUserId(state: IIdentityMonitoringState, userId: string): IFamilyMember {
  let member = getITPFamilyMemberFromUserId(state, userId);
  if (!member) {
    member = createDefaultFamilyMember({ userId });
    state.familyMembers.push(member);
  }
  return member;
}

const setMemberBreaches = (state: IIdentityMonitoringState, csid: string, newBreaches: BreachedDetail[], shouldMerge: boolean) => {
  const member = ensureITPFamilyMemberWithCsid(state, csid);
  const breaches = shouldMerge ? mergeBreaches(member.breaches, newBreaches) : buildBreaches(newBreaches);
  const augmentedBreaches = breaches.map(augmentBreach);

  member.breaches = breaches;
  member.totalBreachCount = breaches.length;
  member.activeBreachCount = augmentedBreaches.filter((b) => b.state === 'Incomplete').length;

  return member;
};

const setOneTimeScanBreaches = (newBreaches: BreachedDetail[]) => {
  const breaches = buildBreaches(newBreaches);
  const augmentedBreaches = breaches.map(augmentBreach);
  return augmentedBreaches;
};

const setUnauthOneTimeScanBreaches = (newBreaches: BreachedDetail[]) => {
  const breaches = buildBreaches(newBreaches);
  const augmentedBreaches = breaches.map(augmentBreach);
  return augmentedBreaches;
};

export const getFamilyITPSummaryAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getFamilyITPSummaryAsync',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (options?: { silent?: boolean } | undefined) =>
    microsoftConsumerSecurityClient.identityTheftProtectionFamily.getAllFamilyUsersItpSummary(),
  ({ identityMonitoring }) => identityMonitoring.familyMembersLoadingStatus
);

const handleFamilyITPSummaryAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getFamilyITPSummaryAsync.pending, (state, { meta }) => {
      const silent = Boolean(meta.arg?.silent);
      if (!silent) {
        state.familyMembersLoadingStatus = ApiCallResultStatus.LOADING;
      }
    })
    .addCase(getFamilyITPSummaryAsync.fulfilled, (state, action) => {
      const newFamilyMembers: IFamilyMember[] = [];
      action.payload
        .filter((userSummary) => {
          const isFamilyMemberOnboarded = userSummary.itpOnboardingStatus === 'Monitoring';
          const isITPSupported = isFamilyMemberOnboarded || userSummary.isITPOnboardingSupported;
          return isITPSupported; // hide family members who are not eligible for ITM (#40964035)
        })
        .forEach((userSummary) => {
          let familyMember: IFamilyMember | undefined;
          if (userSummary.csid) {
            familyMember = getITPFamilyMemberFromCsid(state, userSummary.csid);
          } else if (userSummary.userId) {
            familyMember = getITPFamilyMemberFromUserId(state, userSummary.userId);
          }
          if (familyMember) {
            newFamilyMembers.push({ ...familyMember, ...userSummary });
          } else {
            newFamilyMembers.push(createDefaultFamilyMember(userSummary));
          }
        });
      state.familyMembers = newFamilyMembers;
      state.familyMembersLoadingStatus = ApiCallResultStatus.SUCCESS;

      const { userContext } = state;
      const userContextMemberByUserId = newFamilyMembers.find((m) => localeIgnoreCaseEquals(m.userId, userContext.userId)); // find member by userId
      const userContextMemberByCsid = newFamilyMembers.find((m) => localeIgnoreCaseEquals(m.csid, userContext.csid)); // find member by csid
      // set csid in current userContext if there it is not yet set (i.e. when user (family member) never logged in, and is registered the first time)
      userContext.csid = userContext.csid || userContextMemberByUserId?.csid;
      userContext.displayName =
        userContext.displayName || userContextMemberByCsid?.userInfo?.displayName || userContextMemberByUserId?.userInfo?.displayName;
      userContext.userId = userContext.userId || userContextMemberByCsid?.userId;
    })
    .addCase(getFamilyITPSummaryAsync.rejected, (state, action) => {
      state.familyMembersLoadingStatus = ApiCallResultStatus.FAILED;
      state.familyMembersLoadError = action.error.message;
    });
};

export const requestITPPermissionAsync = createAsyncApiThunk(
  'identityMonitoring/requestPermissionAsync',
  (memberUserId: string | undefined) =>
    ensureId(memberUserId, (ensuredUserId) =>
      microsoftConsumerSecurityClient.identityTheftProtectionFamily.requestFamilyMemberConsent({
        body: { memberUserId: ensuredUserId },
      })
    )
);

const handleRequestITPPermissionAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(requestITPPermissionAsync.fulfilled, (state, { meta }) => {
    const memberUserId = meta.arg as string;
    const member = ensureITPFamilyMemberWithUserId(state, memberUserId);
    member.consentsRequested = (member.consentsRequested || 0) + 1;
  });
};

export const deleteSubscriptionAsync = createAsyncApiThunk(
  'identityMonitoring/deleteSubscriptionAsync',
  (csid: string | undefined, thunkApi) =>
    ensureId(csid, async (ensuredCsid) => {
      const result = pageDataService.isCurrentUser(ensuredCsid)
        ? await microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.deleteSubscriber({ forceDelete: true })
        : await microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscription.deleteSubscriber(ensuredCsid, {
            forceDelete: true,
          });

      const allUserSummary = await microsoftConsumerSecurityClient.identityTheftProtectionFamily.getAllFamilyUsersItpSummary();
      thunkApi.dispatch(resetAlerts());
      return { result, userSummary: allUserSummary.find((us) => localeIgnoreCaseEquals(us.csid, ensuredCsid)) };
    })
);

const handleDeleteSubscriptionAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(deleteSubscriptionAsync.pending, (state, { meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.deleteSubscriptionApiStatus = { requestId: meta.requestId, status: ApiCallResultStatus.LOADING };
    })
    .addCase(deleteSubscriptionAsync.fulfilled, (state, { payload, meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      if (payload.userSummary && member.deleteSubscriptionApiStatus?.requestId === meta.requestId) {
        const updatedMember = createDefaultFamilyMember(payload.userSummary);
        updatedMember.deleteSubscriptionApiStatus = { requestId: meta.requestId, status: ApiCallResultStatus.SUCCESS };
        const index = state.familyMembers.findIndex((m) => localeIgnoreCaseEquals(csid, m.csid));
        if (index >= 0) {
          state.familyMembers[index] = updatedMember;
        } else {
          state.familyMembers.push(updatedMember);
        }
      }
    })
    .addCase(deleteSubscriptionAsync.rejected, (state, { meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      if (member.deleteSubscriptionApiStatus?.requestId === meta.requestId) {
        member.deleteSubscriptionApiStatus.status = ApiCallResultStatus.FAILED;
      }
    });
};

export const getCompleteSubscriberProfileAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getCompleteSubscriberProfileAsync',
  (csid: string | undefined) => {
    return ensureId(csid, async (ensuredCsid) => {
      const { disclosureDateTime, futureDeactivationDate, futureActivationDate, dob, ...response } = pageDataService.isCurrentUser(
        ensuredCsid
      )
        ? await microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.getCompleteSubscriberProfile()
        : await microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscription.getCompleteSubscriberProfile(ensuredCsid);

      return {
        ...response,
        // avoid mutable types (i.e. Date) in payload
        disclosureDateTime: disclosureDateTime?.toISOString(),
        futureDeactivationDate: futureDeactivationDate?.toISOString(),
        futureActivationDate: futureActivationDate?.toISOString(),
        dob: formatUTCDateISO(dob),
      };
    });
  },
  ({ identityMonitoring }, csid) =>
    getITPFamilyMemberFromCsid(identityMonitoring, csid)?.getCompleteSubscriberProfileApiStatus ?? ApiCallResultStatus.DEFAULT
);

const handleGetSubscriberCompleteProfileAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getCompleteSubscriberProfileAsync.pending, (state, { meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.getCompleteSubscriberProfileApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getCompleteSubscriberProfileAsync.fulfilled, (state, { payload, meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);

      const {
        cyberData,
        ssnAvailable,
        isSSNVerified,
        ssnValidationStatus,
        notifications,
        unverifiedContactInfo,
        dob,
        fullName,
        fullAddress,
        msaData,
      } = payload;
      member.getCompleteSubscriberProfileApiStatus = ApiCallResultStatus.SUCCESS;

      // SSN Validation Status
      member.ssnValidationStatus = ssnValidationStatus;

      // monitoring data
      const { subscriberProfile } = member;

      subscriberProfile.cyberData = cyberData ?? {};
      subscriberProfile.unverifiedContactInfo = unverifiedContactInfo ?? {};

      // subscriber primary email
      // TODO: notification will support non-primary email as a preference, we will need to update this to get the proper primary email (#37330680)
      member.primaryEmailAddress = notifications?.emailAddress;

      if (member.primaryEmailAddress) {
        subscriberProfile.cyberData.emailAddresses ??= [];
        subscriberProfile.cyberData.emailAddresses.push(member.primaryEmailAddress);
      }

      // whether SSN has been added for this subscriber
      subscriberProfile.isSSNAdded = !!ssnAvailable;
      // whether SSN has been verified for this subscriber
      subscriberProfile.isSSNVerified = isSSNVerified;

      // personal info
      subscriberProfile.dob = dob;
      subscriberProfile.fullName = fullName;
      subscriberProfile.fullAddress = fullAddress;

      // msa data
      subscriberProfile.msaData = {
        emailAddresses: {
          dataType: 'EmailAddress',
          values:
            msaData?.emailAddresses
              ?.filter((data): data is { emailAddress: string; isVerified?: boolean } => !!data.emailAddress)
              .map(({ emailAddress, isVerified }) => ({ email: emailAddress, isVerified: !!isVerified })) ?? [],
        },
        telephoneNumbers: {
          dataType: 'TelephoneNumber',
          values:
            msaData?.telephoneNumbers
              ?.filter((data): data is { telephoneNumber: string; isVerified?: boolean } => !!data.telephoneNumber)
              .map(({ telephoneNumber, isVerified }) => ({ telephone: telephoneNumber, isVerified: !!isVerified })) ?? [],
        },
      };
    })
    .addCase(getCompleteSubscriberProfileAsync.rejected, (state, { meta }) => {
      const csid = meta.arg as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.getCompleteSubscriberProfileApiStatus = ApiCallResultStatus.FAILED;
    });
};

export const registerSubscriberWithPrimaryDataAsync = createAsyncApiThunk(
  'identityMonitoring/registerSubscriberWithPrimaryDataAsync',
  ({ userId, ...userInfo }: RegisterSubscriberWithPrimaryDataRequest & { userId: string | undefined }) =>
    ensureId(userId, (ensuredUserId) =>
      pageDataService.isCurrentUserByUserId(ensuredUserId)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.registerSubscriberWithPrimaryData({
            body: userInfo,
          })
        : microsoftConsumerSecurityClient.identityTheftProtectionFamily.registerSubscriberWithPrimaryData({
            body: { memberUserId: ensuredUserId, dateOfBirth: userInfo.dateOfBirth, fullName: userInfo.fullName },
          })
    )
);

export const registerSubscriberAsync = createAsyncApiThunk(
  'identityMonitoring/registerSubscriberAsync',
  (userId: string | undefined) =>
    ensureId(userId, (ensuredUserId) =>
      pageDataService.isCurrentUserByUserId(ensuredUserId)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.registerSubscriber()
        : microsoftConsumerSecurityClient.identityTheftProtectionFamily.registerSubscriber({ body: { memberUserId: ensuredUserId } })
    )
);

export const deleteCyberDataElementsAsync = createAsyncApiThunk(
  'identityMonitoring/deleteCyberDataElementsAsync',
  ({ csid, ...deleteCyberDataRequest }: DeleteSubscriberCyberDataRequest & { csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.deleteCyberDataElements({
            body: deleteCyberDataRequest,
          })
        : microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscription.deleteCyberDataElements(ensuredCsid, {
            body: deleteCyberDataRequest,
          })
    )
);

export const generateEmailVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/generateEmailVerificationCodeAsync',
  ({ email, csid }: { email: string; csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.emailVerification.generateCode({ body: email })
        : microsoftConsumerSecurityClient.familyEmailVerification.generateCode(ensuredCsid, { body: email })
    )
);

const handleGenerateEmailVerificationCodeAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(generateEmailVerificationCodeAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.GENERATING_CODE;
    })
    .addCase(generateEmailVerificationCodeAsync.fulfilled, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.CODE_UNVERIFIED;
    })
    .addCase(generateEmailVerificationCodeAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.GENERATION_ERROR;
    });
};

export const verifyEmailVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/verifyEmailVerificationCodeAsync',
  ({
    csid,
    updateExternal,
    ...body
  }: {
    emailAddress: string;
    securityCode: string;
    csid: string | undefined;
    updateExternal: boolean;
  }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.emailVerification.verifyCode({ body, updateExternal })
        : microsoftConsumerSecurityClient.familyEmailVerification.verifyCode(ensuredCsid, { body, updateExternal })
    )
);

const handleVerifyEmailVerificationCodeAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(verifyEmailVerificationCodeAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.VERIFYING;
    })
    .addCase(verifyEmailVerificationCodeAsync.fulfilled, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.VALID;
    })
    .addCase(verifyEmailVerificationCodeAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.INVALID;
    });
};

export const resetEmailVerificationCodeStatus = createAction<string | undefined>(
  'identityMonitoring/resetEmailVerificationCodeStatus'
);

const handleResetEmailCodeVerificationStatus = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetEmailVerificationCodeStatus, (state, { payload }) => {
    ensureId(payload, async (ensuredCsid) => {
      const member = ensureITPFamilyMemberWithCsid(state, ensuredCsid);
      member.emailCodeVerificationStatus = CodeVerificationStatus.DEFAULT;
    });
  });
};

export const generatePhoneVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/generatePhoneVerificationCodeAsync',
  ({ countryCode, phoneNumber, csid }: PhoneGenerateCodeRequest & { csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.phoneVerification.generateCode({ body: { countryCode, phoneNumber } })
        : microsoftConsumerSecurityClient.familyPhoneVerification.generateCode(ensuredCsid, { body: { countryCode, phoneNumber } })
    )
);

const handleGeneratePhoneVerificationCodeAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(generatePhoneVerificationCodeAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.GENERATING_CODE;
    })
    .addCase(generatePhoneVerificationCodeAsync.fulfilled, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.CODE_UNVERIFIED;
    })
    .addCase(generatePhoneVerificationCodeAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.GENERATION_ERROR;
    });
};

export const verifyPhoneVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/verifyPhoneVerificationCodeAsync',
  ({
    countryCode,
    phoneNumber,
    securityCode,
    csid,
    updateExternal,
  }: PhoneVerificationCodeRequest & { csid: string | undefined } & { updateExternal: boolean }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.phoneVerification.verifyCode({
            body: { countryCode, phoneNumber, securityCode },
            updateExternal,
          })
        : microsoftConsumerSecurityClient.familyPhoneVerification.verifyCode(ensuredCsid, {
            body: { countryCode, phoneNumber, securityCode },
            updateExternal,
          })
    )
);

const handleVerifyPhoneVerificationCodeAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(verifyPhoneVerificationCodeAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.VERIFYING;
    })
    .addCase(verifyPhoneVerificationCodeAsync.fulfilled, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.VALID;
    })
    .addCase(verifyPhoneVerificationCodeAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.INVALID;
    });
};

export const resetPhoneVerificationCodeStatus = createAction<string | undefined>(
  'identityMonitoring/resetPhoneVerificationCodeStatus'
);

const handleResetPhoneCodeVerificationStatus = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetPhoneVerificationCodeStatus, (state, { payload }) => {
    ensureId(payload, async (ensuredCsid) => {
      const member = ensureITPFamilyMemberWithCsid(state, ensuredCsid);
      member.phoneCodeVerificationStatus = CodeVerificationStatus.DEFAULT;
    });
  });
};

export const updateSubscriberProfileAsync = createAsyncApiThunk(
  'identityMonitoring/updateSubscriberProfileAsync',
  ({ csid, ...updateSubscriberRequest }: UpdateSubscriberRequest & { csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.updateSubscriberProfile({
            body: updateSubscriberRequest,
          })
        : microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscription.updateSubscriberProfile(ensuredCsid, {
            body: updateSubscriberRequest,
          })
    )
);

export const updateSubscriberProfileSettingsAsync = createAsyncApiThunk(
  'identityMonitoring/updateSubscriberProfileSettingAsync',
  async ({ ...itmUserProfilePatchRequest }: ItmUserProfilePatchRequest, thunkApi) => {
    microsoftConsumerSecurityClient.identityTheftMonitoringSubscription.updateSubscriberProfileSettings({
      body: itmUserProfilePatchRequest,
    });

    // Refresh the ITP summary after updating the profile settings to refresh credit score data
    thunkApi.dispatch(getFamilyITPSummaryAsync.forceRequest() as unknown as AnyAction);
  }
);

export const getSSNVerificationQuestionsAsync = createAsyncApiThunk(
  'identityMonitoring/getSSNVerificationQuestionsAsync',
  ({ csid, ...request }: GetIdVerificationApiRequest & { csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscriptionAuthentication.getIDVerification({
            body: request,
          })
        : microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscriptionAuthentication.getIDVerification(ensuredCsid, {
            body: request,
          })
    )
);

const handleGetSSNVerificationQuestionsAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getSSNVerificationQuestionsAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.ssnVerification = { requestId: meta.requestId, status: SSNVerificationStatus.PENDING_GET_QUESTIONS };
    })
    .addCase(getSSNVerificationQuestionsAsync.fulfilled, (state, { payload, meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      const { requestId } = meta;
      if (member.ssnVerification.requestId === requestId) {
        member.ssnVerification = { requestId, status: SSNVerificationStatus.SUCCESS_GET_QUESTIONS, questions: payload };
      }
    })
    .addCase(getSSNVerificationQuestionsAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      const { requestId } = meta;
      if (member.ssnVerification.requestId === requestId) {
        member.ssnVerification = { requestId, status: SSNVerificationStatus.FAILED_GET_QUESTIONS };
      }
    });
};

export const submitSSNVerificationAnswersAsync = createAsyncApiThunk(
  'identityMonitoring/submitSSNVerificationAnswersAsync',
  ({ csid, ...request }: SubmitIdVerificationDataApiRequest & { csid: string | undefined }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.identityTheftMonitoringSubscriptionAuthentication.submitIDVerification({
            body: request,
          })
        : microsoftConsumerSecurityClient.familyIdentityTheftMonitoringSubscriptionAuthentication.submitIDVerification(ensuredCsid, {
            body: request,
          })
    )
);

const handleSubmitSSNVerificationAnswersAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(submitSSNVerificationAnswersAsync.pending, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      member.ssnVerification = { requestId: meta.requestId, status: SSNVerificationStatus.PENDING_SUBMIT_ANSWERS };
    })
    .addCase(submitSSNVerificationAnswersAsync.fulfilled, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      const { requestId } = meta;
      if (member.ssnVerification.requestId === requestId) {
        member.ssnVerification = { requestId, status: SSNVerificationStatus.SUCCESS_SUBMIT_ANSWERS };
      }
    })
    .addCase(submitSSNVerificationAnswersAsync.rejected, (state, { meta }) => {
      const csid = meta.arg.csid as string;
      const member = ensureITPFamilyMemberWithCsid(state, csid);
      const { requestId } = meta;
      if (member.ssnVerification.requestId === requestId) {
        member.ssnVerification = { requestId, status: SSNVerificationStatus.FAILED_SUBMIT_ANSWERS };
      }
    });
};

export const resetSSNVerificationStatus = createAction(
  'identityMonitoring/resetSSNVerificationStatus',
  (csid: string | undefined) => ({
    payload: csid,
  })
);

const handleResetSSNVerificationStatus = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetSSNVerificationStatus, (state, { payload }) => {
    ensureId(payload, async (ensuredCsid) => {
      const member = ensureITPFamilyMemberWithCsid(state, ensuredCsid);
      member.ssnVerification = { status: SSNVerificationStatus.DEFAULT };
    });
  });
};

/**
 * BREACHES ACTIONS/REDUCERS
 */

/**
 * Trigger a scan and get the reported breaches
 */
export const scanAndGetBreachesAsync = createAsyncApiThunk(
  'identityMonitoring/scanAndGetBreachesAsync',
  ({ csid, ...scanRequest }: { csid?: string } & ScanRequest, thunkApi) =>
    ensureId(csid, async (ensuredCsid) => {
      const breachedDetails = pageDataService.isCurrentUser(ensuredCsid)
        ? await microsoftConsumerSecurityClient.breaches.scanAndGetBreaches({ body: scanRequest })
        : await microsoftConsumerSecurityClient.familyBreaches.scanAndGetBreaches(ensuredCsid, { body: scanRequest });

      thunkApi.dispatch(resetAlerts());
      thunkApi.dispatch(getFamilyITPSummaryAsync.forceRequest() as unknown as AnyAction);
      return breachedDetails;
    })
);

const handleScanAndGetBreachesAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(scanAndGetBreachesAsync.fulfilled, (state, { payload, meta }) => {
    const { csid } = meta.arg;
    csid && setMemberBreaches(state, csid, payload, true);
  });
};

/**
 * Fetch the breaches (without triggering a new scan)
 */
export const getBreachesAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getBreachesAsync',
  ({ csid, delayInMs }: { csid?: string | undefined; delayInMs?: number }) =>
    ensureId(csid, async (ensuredCsid) =>
      new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, delayInMs ?? 0);
      }).then(() => {
        return pageDataService.isCurrentUser(ensuredCsid)
          ? microsoftConsumerSecurityClient.breaches.getBreaches()
          : microsoftConsumerSecurityClient.familyBreaches.getBreaches(ensuredCsid);
      })
    ),
  ({ identityMonitoring }, { csid }) =>
    getITPFamilyMemberFromCsid(identityMonitoring, csid)?.getBreachesApiStatus ?? ApiCallResultStatus.DEFAULT
);

const handleGetBreachesAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getBreachesAsync.pending, (state, { meta }) => {
      const { csid } = meta.arg;
      const member = ensureITPFamilyMemberWithCsid(state, csid!);
      member.getBreachesApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getBreachesAsync.fulfilled, (state, { payload, meta }) => {
      const { csid } = meta.arg;
      const member = setMemberBreaches(state, csid!, payload, false);
      member.getBreachesApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(getBreachesAsync.rejected, (state, { meta }) => {
      const { csid } = meta.arg;
      const member = ensureITPFamilyMemberWithCsid(state, csid!);
      member.getBreachesApiStatus = ApiCallResultStatus.FAILED;
    });
};

/**
 * Get a breach by id
 */
export const getBreachByIdAsync = createAsyncApiThunk(
  'identityMonitoring/getBreachByIdAsync',
  async ({ breachId, csid }: { breachId: string; csid: string }) => {
    const result = pageDataService.isCurrentUser(csid)
      ? await microsoftConsumerSecurityClient.breaches.getBreachById(breachId)
      : await microsoftConsumerSecurityClient.familyBreaches.getBreachById(breachId, csid);

    return result;
  }
);

const handleGetBreachByIdAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(getBreachByIdAsync.fulfilled, (state, { payload, meta }) => {
    const { csid } = meta.arg;
    setMemberBreaches(state, csid, [payload], true);
  });
};
/**
 * Invoke {@link fn} if breach state changes after updating entities of {@link updateEntityIds} to state {@link newEntityState}
 *
 * @param state
 * @param breachId
 * @param csid
 * @param updateEntityIds
 * @param newEntityState
 * @param fn
 */
const ifBreachStateChanged = (
  state: IIdentityMonitoringState,
  breachId: string,
  csid: string | undefined,
  updateEntityIds: string[],
  newEntityState: BreachEntityState,
  fn: () => void
) => {
  // Current breach from store
  const currentBreach = getBreachById(state, breachId, csid);

  if (currentBreach) {
    // Calculates the new state of the breach where entities of IDs from `updateEntityIds` are considered in state `newEntityState`
    const newBreachState: BreachState = extractBreachEntities(currentBreach)
      .map((entity) => (updateEntityIds.includes(entity.entityId) ? newEntityState : entity.entityState))
      .some((entityState) => entityState === 'New')
      ? 'Incomplete'
      : 'Complete';

    if (currentBreach.state !== newBreachState) {
      // Invoke `fn` if current and new states different
      fn();
    }
  }
};

/**
 * Update state of a breach entity
 */
export const updateBreachEntityStateAsync = createAsyncApiThunk(
  'identityMonitoring/updateBreachEntityStateAsync',
  (
    {
      breachId,
      entityId,
      breachedType,
      state,
      csid,
    }: {
      breachId: string;
      entityId: string;
      breachedType: BreachEntityType;
      state: BreachEntityState;
      csid?: string;
    },
    thunkApi
  ) =>
    ensureId(csid, async (ensuredCsid) => {
      const breach = pageDataService.isCurrentUser(ensuredCsid)
        ? await microsoftConsumerSecurityClient.breaches.updateBreachEntityState(breachId, entityId, {
            breachedType,
            state,
          })
        : await microsoftConsumerSecurityClient.familyBreaches.updateBreachEntityState(breachId, ensuredCsid, entityId, {
            breachedType,
            state,
          });

      ifBreachStateChanged(thunkApi.getState().identityMonitoring, breachId, csid, [entityId], state, () => {
        thunkApi.dispatch(resetAlerts());
        // refresh ITP family summary
        thunkApi.dispatch(getFamilyITPSummaryAsync.forceRequest() as unknown as AnyAction);
      });

      return breach;
    })
);

const handleUpdateBreachEntityStateAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(updateBreachEntityStateAsync.pending, (state, { meta }) => {
      const { breachId, entityId, csid } = meta.arg;
      const entity = getBreachEntityById(state, breachId, entityId, csid);
      if (entity) {
        entity.entityStateApiStatus = ApiCallResultStatus.LOADING;
      }
    })
    .addCase(updateBreachEntityStateAsync.fulfilled, (state, { meta, payload }) => {
      const { breachId, entityId, csid } = meta.arg;
      const entity = getBreachEntityById(state, breachId, entityId, csid);
      if (entity) {
        const responseEntity =
          payload.breachEntities?.find((e) => e.entityId === entity.entityId) ??
          payload.compositeBreachEntities?.find((e) => e.entityId === entity.entityId);
        const entityState = responseEntity?.state;
        const lastModifiedDate = responseEntity?.lastModifiedDate;

        entity.entityState = entityState ?? entity.entityState;
        entity.entityStateApiStatus = ApiCallResultStatus.SUCCESS;
        entity.lastModifiedDate = lastModifiedDate ?? entity.lastModifiedDate;
      }
    })
    .addCase(updateBreachEntityStateAsync.rejected, (state, { meta }) => {
      const { breachId, entityId, csid } = meta.arg;
      const entity = getBreachEntityById(state, breachId, entityId, csid);
      if (entity) {
        entity.entityStateApiStatus = ApiCallResultStatus.FAILED;
      }
    });
};

/**
 * Update states of breaches in batch
 */
export const batchUpdateBreachStateAsync = createAsyncApiThunk(
  'identityMonitoring/batchUpdateBreachStateAsync',
  ({ breachIds, state, csid }: { breachIds: string[]; state: BreachEntityState; csid?: string }, thunkApi) =>
    ensureId(csid, async (ensuredCsid) => {
      const breaches = pageDataService.isCurrentUser(ensuredCsid)
        ? await microsoftConsumerSecurityClient.breaches.patchBreaches({ body: { breachIds, state } })
        : await microsoftConsumerSecurityClient.familyBreaches.patchBreaches(ensuredCsid, { body: { breachIds, state } });

      // API returns only updated breaches
      if (breaches.length > 0) {
        // reset alerts
        thunkApi.dispatch(resetAlerts());
        // refresh ITP family summary
        thunkApi.dispatch(getFamilyITPSummaryAsync.forceRequest() as unknown as AnyAction);
      }

      return breaches;
    })
);

const handleBatchUpdateBreachStateAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(batchUpdateBreachStateAsync.pending, (state, { meta }) => {
      const { breachIds, csid } = meta.arg;
      getBreaches(state, csid).allBreaches.forEach((breach) => {
        if (breachIds.includes(breach.breachId)) {
          // eslint-disable-next-line no-param-reassign
          breach.breachStateApiStatus = ApiCallResultStatus.LOADING;
        }
      });
    })
    .addCase(batchUpdateBreachStateAsync.fulfilled, (state, { meta, payload }) => {
      const { breachIds, csid } = meta.arg;

      // get entities from the updated breaches
      const entityStates: Record<string, BreachEntity | undefined> = fromPairs(
        payload.flatMap((breachedDetail) => [
          ...(breachedDetail.singleBreachEntities?.map((e) => [e.entityId, e]) ?? []),
          ...(breachedDetail.compositeBreachEntities?.map((e) => [e.entityId, e]) ?? []),
        ])
      );

      getBreaches(state, csid).allBreaches.forEach((breach) => {
        if (breachIds.includes(breach.breachId)) {
          // eslint-disable-next-line no-param-reassign
          breach.breachStateApiStatus = ApiCallResultStatus.SUCCESS;

          // update entity states in breach
          extractBreachEntities(breach).forEach((entity) => {
            const updatedEntity = entityStates[entity.entityId];
            // eslint-disable-next-line no-param-reassign
            entity.entityState = updatedEntity?.state ?? entity.entityState;
            // eslint-disable-next-line no-param-reassign
            entity.lastModifiedDate = updatedEntity?.lastModifiedDate ?? entity.lastModifiedDate;
          });
        }
      });

      const member = getITPFamilyMemberFromCsid(state, csid);
      if (member) {
        member.activeBreachCount = getBreaches(state, csid).allBreaches.filter((b) => b.state === 'Incomplete').length;
      }
    })
    .addCase(batchUpdateBreachStateAsync.rejected, (state, { meta }) => {
      const { breachIds, csid } = meta.arg;
      getBreaches(state, csid).allBreaches.forEach((breach) => {
        if (breachIds.includes(breach.breachId)) {
          // eslint-disable-next-line no-param-reassign
          breach.breachStateApiStatus = ApiCallResultStatus.FAILED;
        }
      });
    });
};

/**
 * Update state of a breach entities in batch
 */
export const batchUpdateBreachEntityStateAsync = createAsyncApiThunk(
  'identityMonitoring/batchUpdateBreachEntityStateAsync',
  (
    { breachId, entityIds, state, csid }: { breachId: string; entityIds: string[]; state: BreachEntityState; csid?: string },
    thunkApi
  ) =>
    ensureId(csid, async (ensuredCsid) => {
      const breach = pageDataService.isCurrentUser(ensuredCsid)
        ? await microsoftConsumerSecurityClient.breaches.batchUpdateBreachEntityState(breachId, {
            body: { entityIds, state },
          })
        : await microsoftConsumerSecurityClient.familyBreaches.batchUpdateBreachEntityState(breachId, ensuredCsid, {
            body: { entityIds, state },
          });

      ifBreachStateChanged(thunkApi.getState().identityMonitoring, breachId, csid, entityIds, state, () => {
        thunkApi.dispatch(resetAlerts());
        // refresh ITP family summary
        thunkApi.dispatch(getFamilyITPSummaryAsync.forceRequest() as unknown as AnyAction);
      });

      return breach;
    })
);

const handleBatchUpdateBreachEntityStateAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(batchUpdateBreachEntityStateAsync.pending, (state, { meta }) => {
      const { breachId, entityIds, csid } = meta.arg;
      const entities = getBreachEntities(state, breachId, csid).filter((entity) => entityIds.includes(entity.entityId));

      entities.forEach((entity) => {
        // eslint-disable-next-line no-param-reassign
        entity.entityStateApiStatus = ApiCallResultStatus.LOADING;
      });
    })
    .addCase(batchUpdateBreachEntityStateAsync.fulfilled, (state, { meta, payload }) => {
      const { breachId, entityIds, csid } = meta.arg;
      const entities = getBreachEntities(state, breachId, csid).filter((entity) => entityIds.includes(entity.entityId));

      const member = getITPFamilyMemberFromCsid(state, csid);
      const breach = findBreach(member?.breaches, breachId);
      if (breach) {
        breach.breachRecommendations = payload.breachRecommendations ?? breach.breachRecommendations;
      }

      entities.forEach((entity) => {
        const responseEntity =
          payload.singleBreachEntities?.find((e) => e.entityId === entity.entityId) ??
          payload.compositeBreachEntities?.find((e) => e.entityId === entity.entityId);
        const entityState = responseEntity?.state;
        const lastModifiedDate = responseEntity?.lastModifiedDate;

        // eslint-disable-next-line no-param-reassign
        entity.entityState = entityState ?? entity.entityState;
        // eslint-disable-next-line no-param-reassign
        entity.entityStateApiStatus = ApiCallResultStatus.SUCCESS;
        // eslint-disable-next-line no-param-reassign
        entity.lastModifiedDate = lastModifiedDate ?? entity.lastModifiedDate;
      });
    })
    .addCase(batchUpdateBreachEntityStateAsync.rejected, (state, { meta }) => {
      const { breachId, entityIds, csid } = meta.arg;
      const entities = getBreachEntities(state, breachId, csid).filter((entity) => entityIds.includes(entity.entityId));

      entities.forEach((entity) => {
        // eslint-disable-next-line no-param-reassign
        entity.entityStateApiStatus = ApiCallResultStatus.FAILED;
      });
    });
};

/**
 * Updates the state of breach recommendation
 */
export const completeBreachRecommendationAsync = createAsyncApiThunk(
  'identityMonitoring/completeBreachRecommendationAsync',
  ({
    breachId,
    recommendationId,
    isCompleted,
    csid,
  }: {
    breachId: string;
    recommendationId: RecommendationId;
    isCompleted: boolean;
    csid?: string;
  }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.breaches.completeBreachRecommendation(breachId, recommendationId, {
            body: { isCompleted },
          })
        : microsoftConsumerSecurityClient.familyBreaches.completeBreachRecommendation(ensuredCsid, breachId, recommendationId, {
            body: { isCompleted },
          })
    )
);

const handleCompleteBreachRecommendationAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  const getRecommendation = (
    state: IIdentityMonitoringState,
    { breachId, recommendationId, csid }: { breachId: string; recommendationId: RecommendationId; csid?: string }
  ) => getBreachRecommendationById(state, breachId, recommendationId, csid);

  builder
    .addCase(completeBreachRecommendationAsync.pending, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        // Optimistically set it first
        recommendation.isCompleted = isCompleted;
      }
    })
    .addCase(completeBreachRecommendationAsync.rejected, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        recommendation.isCompleted = !isCompleted;
      }
    });
};

/**
 * Updates the state of recommendation
 *
 * @todo Remove once {@link completeBreachRecommendationAsync} is fully adopted
 * @deprecated Use {@link completeBreachRecommendationAsync} instead
 */
export const completeRecommendationAsync = createAsyncApiThunk(
  'identityMonitoring/completeRecommendationAsync',
  ({
    breachId,
    entityId,
    recommendationId,
    isCompleted,
    csid,
  }: {
    breachId: string;
    entityId: string;
    recommendationId: RecommendationId;
    isCompleted: boolean;
    csid?: string;
  }) =>
    ensureId(csid, (ensuredCsid) =>
      pageDataService.isCurrentUser(ensuredCsid)
        ? microsoftConsumerSecurityClient.breaches.completeRecommendation(breachId, entityId, recommendationId, {
            body: { isCompleted },
          })
        : microsoftConsumerSecurityClient.familyBreaches.completeRecommendation(breachId, ensuredCsid, entityId, recommendationId, {
            body: { isCompleted },
          })
    )
);

const handleCompleteRecommendationAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  const getRecommendation = (
    state: IIdentityMonitoringState,
    {
      breachId,
      entityId,
      recommendationId,
      csid,
    }: { breachId: string; entityId: string; recommendationId: RecommendationId; csid?: string }
  ) => getEntityRecommendationById(state, breachId, entityId, recommendationId, csid);

  builder
    .addCase(completeRecommendationAsync.pending, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        // Optimistically set it first
        recommendation.isCompleted = isCompleted;
        recommendation.updateRecommendationStateApiStatus = {
          requestId: meta.requestId,
          status: ApiCallResultStatus.LOADING,
        };
      }
    })
    .addCase(completeRecommendationAsync.fulfilled, (state, { meta, payload }) => {
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        const { updateRecommendationStateApiStatus } = recommendation;
        if (updateRecommendationStateApiStatus?.requestId === meta.requestId) {
          updateRecommendationStateApiStatus.status = ApiCallResultStatus.SUCCESS;
          recommendation.isCompleted = Boolean(payload.isCompleted);
        }
      }
    })
    .addCase(completeRecommendationAsync.rejected, (state, { meta }) => {
      const { isCompleted } = meta.arg;
      const recommendation = getRecommendation(state, meta.arg);
      if (recommendation) {
        const { updateRecommendationStateApiStatus } = recommendation;
        if (recommendation && updateRecommendationStateApiStatus?.requestId === meta.requestId) {
          updateRecommendationStateApiStatus.status = ApiCallResultStatus.FAILED;
          recommendation.isCompleted = !isCompleted;
        }
      }
    });
};

export const resetBreachCountsForMember = createAction<string | undefined>('identityMonitoring/resetBreachCountsForMember');

const handleResetBreachCountsForMember = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetBreachCountsForMember, (state, { payload }) => {
    ensureId(payload, async (ensuredCsid) => {
      const member = ensureITPFamilyMemberWithCsid(state, ensuredCsid);

      // make sure that member's activeBreachCount is in sync with breaches states
      const breachStateCounts = getBreachStateCounts(state, ensuredCsid);
      member.activeBreachCount = breachStateCounts?.Incomplete ?? 0;
    });
  });
};

/**
 * One time cyberagent scan
 */
export const getOneTimeScanStatusAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getOneTimeCyberAgentScanStatus',
  async () => {
    const scanStatus = await microsoftConsumerSecurityClient.oneTimeCyberAgentScan.getStatusData();

    return scanStatus;
  },
  ({ identityMonitoring }) => identityMonitoring?.oneTimeScan.statusDataApiStatus || ApiCallResultStatus.DEFAULT
);

const handleGetOneTimeScanStatusAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getOneTimeScanStatusAsync.pending, (state) => {
      state.oneTimeScan.statusDataApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getOneTimeScanStatusAsync.fulfilled, (state, { payload }) => {
      state.oneTimeScan.statusDataApiStatus = ApiCallResultStatus.SUCCESS;
      state.oneTimeScan.statusData = payload;
    })
    .addCase(getOneTimeScanStatusAsync.rejected, (state) => {
      state.oneTimeScan.statusDataApiStatus = ApiCallResultStatus.FAILED;
    });
};

export const generateOneTimeScanEmailVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/generateOneTimeScanEmailVerificationCodeAsync',
  ({ emailAddress }: { emailAddress: string }) =>
    microsoftConsumerSecurityClient.oneTimeCyberAgentScanEmailVerification.generateCode({ body: { emailAddress } })
);

const handleGenerateOneTimeScanEmailVerificationCodeAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(generateOneTimeScanEmailVerificationCodeAsync.pending, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.GENERATING_CODE;
    })
    .addCase(generateOneTimeScanEmailVerificationCodeAsync.fulfilled, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.CODE_UNVERIFIED;
    })
    .addCase(generateOneTimeScanEmailVerificationCodeAsync.rejected, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.GENERATION_ERROR;
    });
};

export const verifyOneTimeScanEmailAsync = createAsyncApiThunk(
  'identityMonitoring/verifyOneTimeScanEmailAsync',
  ({ emailAddress, securityCode }: { emailAddress: string; securityCode: string }) =>
    microsoftConsumerSecurityClient.oneTimeCyberAgentScanEmailVerification.verifyCode({ body: { emailAddress, securityCode } })
);

const handleVerifyOneTimeScanEmailAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(verifyOneTimeScanEmailAsync.pending, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.VERIFYING;
    })
    .addCase(verifyOneTimeScanEmailAsync.fulfilled, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.VALID;
    })
    .addCase(verifyOneTimeScanEmailAsync.rejected, (state) => {
      state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.INVALID;
    });
};

export const resetOneTimeScanEmailVerificationCodeStatus = createAction<string | undefined>(
  'identityMonitoring/resetOneTimeScanEmailVerificationCodeStatus'
);

const handleResetOneTimeScanEmailVerificationCodeStatus = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetOneTimeScanEmailVerificationCodeStatus, (state) => {
    state.oneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.DEFAULT;
  });
};

/**
 * Trigger a one-time scan and get the reported breaches
 */
export const oneTimeScanAndGetBreachesAsync = createAsyncApiThunk(
  'identityMonitoring/oneTimeScanAndGetBreachesAsync',
  async ({ emailAddress }: { emailAddress: string }, thunkApi) => {
    const breachDetails = await microsoftConsumerSecurityClient.oneTimeCyberAgentScan.scanAndGetBreaches({ body: { emailAddress } });

    // get updated scanStatus after scan is completed
    thunkApi.dispatch(getOneTimeScanStatusAsync.forceRequest() as unknown as AnyAction);

    return breachDetails;
  }
);

const handleOneTimeScanAndGetBreachesAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(oneTimeScanAndGetBreachesAsync.pending, (state) => {
      state.oneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(oneTimeScanAndGetBreachesAsync.fulfilled, (state, { payload }) => {
      state.oneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.SUCCESS;
      state.oneTimeScan.breaches = setOneTimeScanBreaches(payload);
    })
    .addCase(oneTimeScanAndGetBreachesAsync.rejected, (state) => {
      state.oneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.FAILED;
    });
};

/**
 * Fetch the breaches for one-time scan (without triggering a new scan)
 */
export const getOneTimeScanBreachesAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getOneTimeScanBreachesAsync',
  async () => {
    const breaches = await microsoftConsumerSecurityClient.oneTimeCyberAgentScan.getExistingBreaches();
    return breaches;
  },
  ({ identityMonitoring }) => identityMonitoring?.oneTimeScan?.getExistingBreachesApiStatus ?? ApiCallResultStatus.DEFAULT
);

const handleGetOneTimeScanBreachesAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getOneTimeScanBreachesAsync.pending, (state) => {
      state.oneTimeScan.getExistingBreachesApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getOneTimeScanBreachesAsync.fulfilled, (state, { payload }) => {
      state.oneTimeScan.breaches = setOneTimeScanBreaches(payload);
      state.oneTimeScan.getExistingBreachesApiStatus = ApiCallResultStatus.SUCCESS;
    })
    .addCase(getOneTimeScanBreachesAsync.rejected, (state) => {
      state.oneTimeScan.getExistingBreachesApiStatus = ApiCallResultStatus.FAILED;
    });
};

/**
 * Unauthenticated One time cyberagent scan
 */
export const getUnauthOneTimeScanStatusAsync = createOneOffAsyncApiThunk(
  'identityMonitoring/getUnauthOneTimeCyberAgentScanStatus',
  async ({ emailAddress }: { emailAddress: string }) => {
    const scanStatus = await microsoftConsumerSecurityClient.unauthOneTimeCyberAgentScan.getStatusData({
      body: { emailAddress },
    });

    return scanStatus;
  },
  ({ identityMonitoring }) => identityMonitoring?.unauthOneTimeScan.statusDataApiStatus || ApiCallResultStatus.DEFAULT
);

const handleGetUnauthOneTimeScanStatusAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getUnauthOneTimeScanStatusAsync.pending, (state) => {
      state.unauthOneTimeScan.statusDataApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getUnauthOneTimeScanStatusAsync.fulfilled, (state, { payload }) => {
      state.unauthOneTimeScan.statusDataApiStatus = ApiCallResultStatus.SUCCESS;
      state.unauthOneTimeScan.statusData = payload;
    })
    .addCase(getUnauthOneTimeScanStatusAsync.rejected, (state) => {
      state.unauthOneTimeScan.statusDataApiStatus = ApiCallResultStatus.FAILED;
    });
};

export const generateUnauthOneTimeScanEmailVerificationCodeAsync = createAsyncApiThunk(
  'identityMonitoring/generateUnauthOneTimeScanEmailVerificationCodeAsync',
  ({ emailAddress }: { emailAddress: string }) =>
    microsoftConsumerSecurityClient.unauthOneTimeCyberAgentScanEmailVerification.generateCode({ body: { emailAddress } })
);

const handleGenerateUnauthOneTimeScanEmailVerificationCodeAsync = (
  builder: ActionReducerMapBuilder<IIdentityMonitoringState>
): void => {
  builder
    .addCase(generateUnauthOneTimeScanEmailVerificationCodeAsync.pending, (state) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.GENERATING_CODE;
    })
    .addCase(generateUnauthOneTimeScanEmailVerificationCodeAsync.fulfilled, (state) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.CODE_UNVERIFIED;
    })
    .addCase(generateUnauthOneTimeScanEmailVerificationCodeAsync.rejected, (state) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.GENERATION_ERROR;
    });
};

export const verifyUnauthOneTimeScanEmailAsync = createAsyncApiThunk(
  'identityMonitoring/verifyUnauthOneTimeScanEmailAsync',
  ({ emailAddress, securityCode }: { emailAddress: string; securityCode: string }) =>
    microsoftConsumerSecurityClient.unauthOneTimeCyberAgentScanEmailVerification.verifyCode({ body: { emailAddress, securityCode } })
);

const handleVerifyUnauthOneTimeScanEmailAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(verifyUnauthOneTimeScanEmailAsync.pending, (state) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.VERIFYING;
    })
    .addCase(verifyUnauthOneTimeScanEmailAsync.fulfilled, (state, { payload }) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.VALID;
      state.unauthOneTimeScan.sessionId = payload.sessionId;
    })
    .addCase(verifyUnauthOneTimeScanEmailAsync.rejected, (state) => {
      state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.INVALID;
    });
};

export const resetUnauthOneTimeScanEmailVerificationCodeStatus = createAction<string | undefined>(
  'identityMonitoring/resetUnauthOneTimeScanEmailVerificationCodeStatus'
);

const handleResetUnauthOneTimeScanEmailVerificationCodeStatus = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder.addCase(resetUnauthOneTimeScanEmailVerificationCodeStatus, (state) => {
    state.unauthOneTimeScan.emailCodeVerificationStatus = CodeVerificationStatus.DEFAULT;
  });
};

/**
 * Trigger an unauthenticated one-time scan and get the reported breaches
 */
export const unauthOneTimeScanAndGetBreachesAsync = createAsyncApiThunk(
  'identityMonitoring/unauthOneTimeScanAndGetBreachesAsync',
  async ({ emailAddress, sessionId }: { emailAddress: string; sessionId?: string }) => {
    // Don't make any real requests if we are skipping verification in a test
    if (!pageDataService.isUnauthOTSEmailVerificationRequired()) {
      return Promise.resolve([]);
    }

    const breachDetails = await microsoftConsumerSecurityClient.unauthOneTimeCyberAgentScan.scanAndGetBreaches({
      body: { emailAddress, sessionId },
    });

    return breachDetails;
  }
);

const handleUnauthOneTimeScanAndGetBreachesAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(unauthOneTimeScanAndGetBreachesAsync.pending, (state) => {
      state.unauthOneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.LOADING;
      state.unauthOneTimeScan.breaches = [];
    })
    .addCase(unauthOneTimeScanAndGetBreachesAsync.fulfilled, (state, { payload }) => {
      state.unauthOneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.SUCCESS;

      if (!pageDataService.isUnauthOTSEmailVerificationRequired()) {
        const testBreaches = [];
        if (pageDataService.isUnauthOneTimeScanUsingFakeBreaches()) {
          for (let i = 0; i < 10; i++) {
            testBreaches.push(testBreachesForOTSv2[0], testBreachesForOTSv2[1]);
          }
        }
        state.unauthOneTimeScan.breaches = testBreaches as AugmentedBreach[];
      } else {
        state.unauthOneTimeScan.breaches = setUnauthOneTimeScanBreaches(payload);
      }
    })
    .addCase(unauthOneTimeScanAndGetBreachesAsync.rejected, (state) => {
      state.unauthOneTimeScan.scanAndGetBreachesApiStatus = ApiCallResultStatus.FAILED;
    });
};

/**
 * Whenever we are onboarding a new user, we get DoB and AgeGroup from IDSAPI.
 */
export const getSelectedUserOnboardingDateOfBirthAsync = createAsyncApiThunk(
  'identityMonitoring/getSelectedUserOnboardingDateOfBirthAsync',
  async (puid: string | undefined) =>
    puid
      ? microsoftConsumerSecurityClient.familyUser.getUserAgeDetails(puid)
      : microsoftConsumerSecurityClient.userProfile.getUserAgeDetails()
);

const handleGetSelectedUserOnboardingDateOfBirthAsync = (builder: ActionReducerMapBuilder<IIdentityMonitoringState>): void => {
  builder
    .addCase(getSelectedUserOnboardingDateOfBirthAsync.pending, (state) => {
      state.userContext.selectedUserOnboarding = {
        ...createDefaultSelectedUserOnboarding(),
        dateOfBirthApiStatus: ApiCallResultStatus.LOADING,
      };
    })
    .addCase(getSelectedUserOnboardingDateOfBirthAsync.fulfilled, (state, { payload }) => {
      state.userContext.selectedUserOnboarding = {
        dateOfBirthApiStatus: ApiCallResultStatus.SUCCESS,
        dateOfBirth: payload.dateOfBirth,
        ageGroup: payload.ageGroup,
      };
    })
    .addCase(getSelectedUserOnboardingDateOfBirthAsync.rejected, (state) => {
      state.userContext.selectedUserOnboarding = {
        ...createDefaultSelectedUserOnboarding(),
        dateOfBirthApiStatus: ApiCallResultStatus.FAILED,
      };
    });
};

export const identityMonitoringSlice = createSlice({
  name: 'identityMonitoring',
  initialState,
  reducers: {
    setUserContext: (state, action: PayloadAction<IItpUserContext>) => {
      state.userContext = action.payload;
    },
    setCurrentUserContext: (state) => {
      const { csid, userId, displayName } = pageDataService.getUserData();
      state.userContext = {
        csid,
        userId,
        isCurrentUser: true,
        displayName,
      };
    },
    /**
     * Resets breach state of family members except the current user
     *
     * @param state
     */
    resetFamilyMemberBreaches: (state) => {
      const { userId } = pageDataService.getUserData();
      state.familyMembers
        .filter((m) => !localeIgnoreCaseEquals(m.userId, userId))
        .forEach((draft) => {
          draft.breaches = [];
          draft.getBreachesApiStatus = ApiCallResultStatus.DEFAULT;
        });
    },
    /**
     * Resets breach state of a member
     *
     * @param state
     * @param action
     */
    resetBreachStateByCsid: (state, action: PayloadAction<string>) => {
      state.familyMembers
        .filter((m) => !localeIgnoreCaseEquals(m.userId, action.payload))
        .forEach((draft) => {
          draft.breaches = [];
          draft.getBreachesApiStatus = ApiCallResultStatus.DEFAULT;
        });
    },
    resetSelectedUserOnboardingCallStatus: (state) => {
      state.userContext.selectedUserOnboarding = {
        ...createDefaultSelectedUserOnboarding(),
      };
    },
  },
  extraReducers: (builder) => {
    handleFamilyITPSummaryAsync(builder);
    handleRequestITPPermissionAsync(builder);
    handleDeleteSubscriptionAsync(builder);
    handleGetSubscriberCompleteProfileAsync(builder);
    handleGenerateEmailVerificationCodeAsync(builder);
    handleVerifyEmailVerificationCodeAsync(builder);
    handleResetEmailCodeVerificationStatus(builder);
    handleGeneratePhoneVerificationCodeAsync(builder);
    handleVerifyPhoneVerificationCodeAsync(builder);
    handleResetPhoneCodeVerificationStatus(builder);
    handleGetSSNVerificationQuestionsAsync(builder);
    handleSubmitSSNVerificationAnswersAsync(builder);
    handleResetSSNVerificationStatus(builder);
    handleScanAndGetBreachesAsync(builder);
    handleGetBreachesAsync(builder);
    handleGetBreachByIdAsync(builder);
    handleUpdateBreachEntityStateAsync(builder);
    handleBatchUpdateBreachStateAsync(builder);
    handleBatchUpdateBreachEntityStateAsync(builder);
    handleCompleteRecommendationAsync(builder);
    handleCompleteBreachRecommendationAsync(builder);
    handleResetBreachCountsForMember(builder);

    // credit monitoring
    handleGetCreditSummaryAsync(builder);
    handleGetCreditAlertByIdAsync(builder);
    handleUpdateCreditAlertAsync(builder);
    handleCompleteCreditRecommendationAsync(builder);

    // one-time scan
    handleGetOneTimeScanStatusAsync(builder);
    handleVerifyOneTimeScanEmailAsync(builder);
    handleGenerateOneTimeScanEmailVerificationCodeAsync(builder);
    handleResetOneTimeScanEmailVerificationCodeStatus(builder);
    handleOneTimeScanAndGetBreachesAsync(builder);
    handleGetOneTimeScanBreachesAsync(builder);

    // unauthenticated one-time scan
    handleGetUnauthOneTimeScanStatusAsync(builder);
    handleVerifyUnauthOneTimeScanEmailAsync(builder);
    handleGenerateUnauthOneTimeScanEmailVerificationCodeAsync(builder);
    handleResetUnauthOneTimeScanEmailVerificationCodeStatus(builder);
    handleUnauthOneTimeScanAndGetBreachesAsync(builder);

    // onboarding date of birth provided via IDSAPI
    handleGetSelectedUserOnboardingDateOfBirthAsync(builder);
  },
});

export const {
  setUserContext,
  setCurrentUserContext,
  resetFamilyMemberBreaches,
  resetBreachStateByCsid,
  resetSelectedUserOnboardingCallStatus,
} = identityMonitoringSlice.actions;

export default identityMonitoringSlice.reducer;
